import { Box } from '@mui/joy';
import { SxProps } from '@mui/joy/styles/types';
import { CustomCellRendererProps } from 'ag-grid-react';

import { DEFAULT_PLACEHOLDER } from 'core/constants';
// Direct import needed to avoid circular dependency:
// CellRendererForAgGrid → core/states/agGridCellUpdate
// agGridServerTable → core/elements → CellRendererForAgGrid
import { useAgGridCellUpdateContext } from 'core/states/agGridCellUpdate';
import { Color, Size } from 'theme/enums';

import { Loader } from './Loader';
import TextEllipsis from './TextEllipsis';
import TextLine from './TextLine';

interface IProps extends CustomCellRendererProps {
  textEllipsis?: boolean;
  maxRows?: number;
  placeholder?: string;
  color?: Color;
  sx?: SxProps;
}

const CellRendererForAgGrid = ({
  value,
  valueFormatted,
  setTooltip,
  node: { id, rowGroupColumn },
  colDef: { colId },
  textEllipsis = true,
  maxRows = 1,
  placeholder = DEFAULT_PLACEHOLDER,
  color,
  sx,
}: IProps) => {
  const { isUpdating } = useAgGridCellUpdateContext();

  const emptyValue = rowGroupColumn || 'select' === colId ? '' : placeholder;
  const displayValue = valueFormatted || value || emptyValue;

  if (!displayValue) {
    return;
  }

  if (isUpdating(id, colId)) {
    return (
      <Box sx={{ display: 'flex', alignItems: 'center' }}>
        <Loader size={Size.SM} style={{ textAlign: 'inherit', margin: 0, display: 'block' }} />
      </Box>
    );
  }

  if (!textEllipsis) {
    return <TextLine text={displayValue} color={color} sx={sx} />;
  }

  return (
    <TextEllipsis
      content={displayValue}
      color={color}
      setTooltip={setTooltip}
      maxRows={maxRows}
      sx={sx}
    />
  );
};

export default CellRendererForAgGrid;
